/*
  VAN MILE "Load more" for product grids.
  Markup: snippets/pagination.liquid (mode: 'load_more'). The button is a real <a href="?page=n">, so crawlers
  and no-JS visitors simply navigate. With JS, the next page is fetched through the Section Rendering API
  (current filters and sort are already in the link), its items are appended to the grid, the status,
  progress bar and link are updated in place, and the URL is replaced with ?page=n.

  History: facets.js re-renders a history entry from state.searchParams on back/forward. The entry keeps the
  params of the page the grid starts on (not ?page=n, which would render page n on its own) plus how far the
  visitor had loaded (state.vmLoadMore), and the freshly rendered element loads the remaining pages again.

  Scroll: when Back (or a reload) builds the page again instead of restoring it from the back/forward cache,
  the server renders ?page=n on its own, a shorter grid than the one the visitor left, and the browser's saved
  scroll position lands in the footer. So opening a card from a grid that holds more than that one page
  records the card in state.vmAnchor (and turns the browser's scroll restoration off for the entry), and the
  rebuilt page scrolls that card into view, or to the top of the grid when the card was on an earlier page
  ("Show previous products" is right there). bfcache restores keep their exact state and are left alone.
*/
if (!customElements.get('vm-load-more')) {
  // Only the grid of the document's first render can be the one the browser rebuilt from history
  let scrollChecked = false;
  let scrollResetArmed = false;

  class VmLoadMore extends HTMLElement {
    connectedCallback() {
      this.link = this.querySelector('[data-load-more-link]');
      this.live = this.querySelector('[data-load-more-live]');
      if (this.bound) return;
      this.bound = true;
      // The page this grid starts on (2+ when the visitor opened ?page=n directly)
      this.startPage = Number(this.dataset.page) || 1;
      if (!scrollChecked) {
        scrollChecked = true;
        // Also on the last page, which has no link. Never in the way of Load more itself.
        try {
          this.restoreScroll();
        } catch (error) {
          console.error(error);
        }
      }
      if (!this.link) return;
      this.onClick = this.onClick.bind(this);
      this.link.addEventListener('click', this.onClick);
      this.restore();
    }

    disconnectedCallback() {
      this.controller?.abort();
    }

    onClick(event) {
      // Let modified clicks open a new tab as usual
      if (
        event.defaultPrevented ||
        event.button > 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      )
        return;
      const sectionId = this.dataset.sectionId;
      const grid = this.getGrid();
      if (!sectionId || !grid || typeof fetch !== 'function') return;
      event.preventDefault();
      if (this.loading) return;
      this.load(grid, sectionId);
    }

    getGrid() {
      return document.getElementById(this.dataset.gridId || 'product-grid');
    }

    static currentUrl() {
      return `${window.location.pathname}${window.location.search}`;
    }

    // Filters + sort of this grid, without page/section params, in a stable order
    static contextKey(href) {
      const params = new URL(href, window.location.href).searchParams;
      params.delete('page');
      params.delete('section_id');
      params.delete('sections');
      params.sort();
      return params.toString();
    }

    // Back/forward: facets.js has just rendered the first page of this entry; load what the visitor had loaded
    async restore() {
      const saved = window.history.state?.vmLoadMore;
      if (!saved || !this.link || typeof fetch !== 'function') return;
      if (saved.key !== VmLoadMore.contextKey(this.link.href)) return;
      const target = Number(saved.page) || 0;
      const sectionId = this.dataset.sectionId;
      const grid = this.getGrid();
      if (!sectionId || !grid || target <= Number(this.dataset.page)) return;

      let loaded = false;
      while (this.link && this.isConnected && Number(this.dataset.page) < target) {
        if (!(await this.load(grid, sectionId, { restoring: true }))) break;
        loaded = true;
      }
      if (loaded && this.isConnected) this.announce();
    }

    // Back/forward or reload that rebuilt this page after Load more (see the header comment)
    restoreScroll() {
      const state = window.history.state;
      const grid = this.getGrid();
      if (!state?.vmLoadMore || !grid || !VmLoadMore.isHistoryLoad()) return;
      if (state.vmLoadMore.key !== VmLoadMore.contextKey(window.location.href)) return;
      // Only when the grid the visitor left started on an earlier page than the one rendered now
      if (VmLoadMore.pageParam(state.searchParams) >= this.startPage) return;

      VmLoadMore.setScrollRestoration('manual');
      const card = VmLoadMore.findCard(grid, state.vmAnchor);
      const target = card || grid.closest('#ProductGridContainer') || grid;
      const scroll = () => {
        if (target.isConnected) target.scrollIntoView({ block: card ? 'center' : 'start' });
      };
      scroll();

      // The entry now holds what the server rendered (this page on its own): facets.js re-renders that on a
      // same-document Back, and the next rebuild of the entry keeps the browser's restoration (same height)
      const gridParams = new URLSearchParams(window.location.search);
      gridParams.delete('section_id');
      gridParams.delete('sections');
      gridParams.set('page', String(this.startPage));
      const nextState = Object.assign({}, state, { searchParams: gridParams.toString() });
      delete nextState.vmAnchor;
      window.history.replaceState(nextState, '');

      // Fonts and images can still shift the layout until load: scroll again then, unless the visitor moved
      const inputs = ['pointerdown', 'keydown', 'wheel', 'touchstart'];
      let moved = false;
      const onInput = () => {
        moved = true;
      };
      inputs.forEach((type) => window.addEventListener(type, onInput, { capture: true, passive: true }));
      const onLoad = () => {
        inputs.forEach((type) => window.removeEventListener(type, onInput, true));
        if (!moved) scroll();
        VmLoadMore.resetScrollRestorationOnInput();
      };
      if (document.readyState === 'complete') onLoad();
      else window.addEventListener('load', onLoad, { once: true });
    }

    // Opening a card from a grid that holds more than this URL renders on its own: remember the card
    static onDocumentClick(event) {
      if (
        event.defaultPrevented ||
        event.button > 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      )
        return;
      const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
      if (!link || (link.target && link.target !== '_self')) return;
      const loadMore = document.querySelector('vm-load-more');
      const grid = loadMore instanceof VmLoadMore ? loadMore.getGrid() : null;
      if (!grid || !grid.contains(link)) return;
      if (!(Number(loadMore.dataset.page) > loadMore.startPage)) return;

      let item = link;
      while (item && item.parentElement !== grid) item = item.parentElement;
      const path = item ? VmLoadMore.cardPath(item) : '';
      if (!path) return;
      window.history.replaceState(Object.assign({}, window.history.state || {}, { vmAnchor: path }), '');
      // Chrome reads the mode from the entry when the rebuilt page loads: set it before leaving
      VmLoadMore.setScrollRestoration('manual');
      VmLoadMore.resetScrollRestorationOnInput();
    }

    // The card's product path: its title link (colour dots link to other products). No query string, as search
    // results carry per-search tracking params.
    static cardPath(item) {
      const link = item.querySelector('.vm-card__link, .vm-result-card__link') || item.querySelector('a[href]');
      const href = link?.getAttribute('href');
      return href ? new URL(href, window.location.href).pathname : '';
    }

    static findCard(grid, path) {
      if (!path) return null;
      return Array.from(grid.children).find((item) => VmLoadMore.cardPath(item) === path) || null;
    }

    static pageParam(searchParams) {
      return Number(new URLSearchParams(searchParams || '').get('page')) || 1;
    }

    // A document built by Back/forward or reload (a bfcache restore runs no script, so never gets here)
    static isHistoryLoad() {
      try {
        const entry = window.performance?.getEntriesByType?.('navigation')?.[0];
        const type = entry ? entry.type : { 1: 'reload', 2: 'back_forward' }[window.performance?.navigation?.type];
        return type === 'back_forward' || type === 'reload';
      } catch (error) {
        return false;
      }
    }

    static setScrollRestoration(mode) {
      try {
        if ('scrollRestoration' in window.history) window.history.scrollRestoration = mode;
      } catch (error) {
        // Unsupported: the browser keeps its own restoration
      }
    }

    // The entry's own restoration comes back on the visitor's next input, once ours is done (later filter
    // entries copy the mode, and their Back should restore the scroll as usual)
    static resetScrollRestorationOnInput() {
      if (scrollResetArmed) return;
      scrollResetArmed = true;
      const inputs = ['pointerdown', 'keydown', 'wheel', 'touchstart'];
      const reset = () => {
        inputs.forEach((type) => window.removeEventListener(type, reset, true));
        scrollResetArmed = false;
        VmLoadMore.setScrollRestoration('auto');
      };
      inputs.forEach((type) => window.addEventListener(type, reset, { capture: true, passive: true }));
    }

    // Resolves true when the page was appended
    async load(grid, sectionId, { restoring = false } = {}) {
      const pageUrl = new URL(this.link.href, window.location.href);
      pageUrl.hash = '';
      const fetchUrl = new URL(pageUrl);
      fetchUrl.searchParams.set('section_id', sectionId);
      // If the visitor filters, sorts or goes back meanwhile, this response is stale
      const startUrl = VmLoadMore.currentUrl();

      this.setLoading(true, grid);
      this.controller = new AbortController();

      try {
        const response = await fetch(fetchUrl.toString(), { signal: this.controller.signal });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (VmLoadMore.currentUrl() !== startUrl || !this.isConnected) return false;
        const sourceGrid = html.getElementById(grid.id);
        const items = sourceGrid ? Array.from(sourceGrid.children) : [];
        if (!items.length) throw new Error('No items in the next page');

        items.forEach((item) => {
          // Appended pages sit below the fold: never compete with what is on screen
          item.querySelectorAll('img').forEach((img) => {
            img.setAttribute('loading', 'lazy');
            img.removeAttribute('fetchpriority');
          });
        });

        grid.append(...items);
        this.update(html.querySelector('vm-load-more'));
        this.replaceUrl(pageUrl);

        if (!restoring) {
          this.announce();
          // The card's title link: its image link comes first in the markup but is tabindex="-1" aria-hidden="true"
          const focusTarget =
            items[0].querySelector('.vm-card__link, .vm-result-card__link') ||
            items[0].querySelector('a[href]:not([tabindex="-1"]):not([aria-hidden="true"])');
          if (focusTarget) focusTarget.focus({ preventScroll: true });
        }
        return true;
      } catch (error) {
        if (error.name === 'AbortError') return false;
        console.error(error);
        // Fall back to a normal page load: the link is real (not while restoring, the visitor did not ask)
        if (!restoring) window.location.assign(pageUrl.toString());
        return false;
      } finally {
        this.setLoading(false, grid);
      }
    }

    update(source) {
      const status = this.querySelector('[data-load-more-status]');
      const sourceStatus = source?.querySelector('[data-load-more-status]');
      if (status && sourceStatus) {
        // The fetched page describes itself ("Showing 49–72 of 250"); the grid holds everything from data-first on
        const template = source.dataset.showing;
        const first = Number(this.dataset.first) || 1;
        const last = source.dataset.last;
        if (template && template.includes('[shown]') && last) {
          status.textContent = template.replace('[shown]', first > 1 ? `${first}–${last}` : last);
        } else {
          status.textContent = sourceStatus.textContent.trim();
        }
      }

      const fill = this.querySelector('[data-load-more-fill]');
      const sourceFill = source?.querySelector('[data-load-more-fill]');
      if (fill && sourceFill)
        fill.style.setProperty('--vm-progress', sourceFill.style.getPropertyValue('--vm-progress'));

      if (source?.dataset.page) this.dataset.page = source.dataset.page;

      const sourceLink = source?.querySelector('[data-load-more-link]');
      if (sourceLink) {
        this.link.setAttribute('href', sourceLink.getAttribute('href'));
      } else {
        this.link.removeEventListener('click', this.onClick);
        this.link.remove();
        this.link = null;
      }
    }

    announce() {
      const status = this.querySelector('[data-load-more-status]');
      if (this.live && status) this.live.textContent = status.textContent;
    }

    replaceUrl(pageUrl) {
      const url = new URL(pageUrl);
      url.searchParams.delete('section_id');
      const search = url.searchParams.toString();
      // facets.js renders state.searchParams on back/forward: point it at the page this grid starts on (no page
      // param for page 1, so Back does not render page n on its own) and remember how far the visitor loaded
      const gridParams = new URLSearchParams(url.searchParams);
      if (this.startPage > 1) gridParams.set('page', String(this.startPage));
      else gridParams.delete('page');
      const state = Object.assign({}, window.history.state || {}, {
        searchParams: gridParams.toString(),
        vmLoadMore: { key: VmLoadMore.contextKey(url.href), page: Number(this.dataset.page) || 0 },
      });
      // A card opened earlier no longer describes where the visitor is
      delete state.vmAnchor;
      window.history.replaceState(state, '', `${url.pathname}${search ? `?${search}` : ''}`);
    }

    setLoading(isLoading, grid) {
      this.loading = isLoading;
      this.classList.toggle('is-loading', isLoading);
      grid.setAttribute('aria-busy', String(isLoading));
      const spinner = this.link?.querySelector('.loading__spinner');
      if (spinner) spinner.classList.toggle('hidden', !isLoading);
      if (this.link) {
        if (isLoading) this.link.setAttribute('aria-disabled', 'true');
        else this.link.removeAttribute('aria-disabled');
      }
    }
  }

  customElements.define('vm-load-more', VmLoadMore);
  document.addEventListener('click', VmLoadMore.onDocumentClick);
}
